import { Card } from 'Card';
import { type ComponentProps, type ReactNode, memo } from 'react';
import { twMerge } from 'tailwind-merge';
import { Text } from 'Text';
import { useTutorBotBottomSpacing } from 'TutorBotChat';
import { parseHtml } from 'FrontRoyalAngular/html.utils';
import clsx from 'clsx';
import { IntroMessages } from './IntroMessages/IntroMessages';
import { MAX_WIDTH_CLASS } from '../constants';

type Props = {
    Button: ReactNode;
    Bottom?: ReactNode;
    cardHeader?: string;
    cardTexts?: string[];
    cardWidth?: ComponentProps<typeof Card>['width'];
    cardDismissible?: boolean;
    onCardDismiss?: () => void;
} & (
    | { type: 'intro'; introMessages: string[]; introWidth?: 'full' | '669' }
    | { type: 'custom-main'; Main: ReactNode; fullHeight?: boolean }
);

function MessagesContainer({
    children,
    fullHeight,
    centered,
}: {
    children: ReactNode;
    fullHeight?: boolean;
    centered?: boolean;
}) {
    return (
        <div
            className={clsx(
                'relative mb-3 flex flex-1 flex-col overflow-y-auto pt-[105px] scrollbar-hide sm:mb-0 sm:pt-11',
                // If the main content is full viewport height, we must add padding to clear the bottom button
                // because the content scrolls behind the button.
                fullHeight && 'h-screen pb-24',
                !fullHeight && 'pb-1',
                centered && 'items-center',
            )}
        >
            {children}
        </div>
    );
}

function introWidthClass(introWidth: 'full' | '669') {
    return {
        full: 'w-full',
        // 669px is the width of the intro messages in the coach page in the `en` locale, in order
        // to have the longest message centered over the button.
        669: 'w-[669px] max-w-full',
    }[introWidth];
}

function BottomAlignedWithButtonComponent(props: Props) {
    const spacingBeneathLowestButton = useTutorBotBottomSpacing();

    return (
        <div className="flex h-full w-full justify-center">
            <div className={clsx(MAX_WIDTH_CLASS, 'relative flex h-full w-full flex-col justify-end')}>
                {props.cardHeader && props.cardTexts && (
                    <div className="absolute top-17.5 z-10 flex w-full justify-center">
                        <Card
                            width={props.cardWidth ?? 342}
                            dismissible={props.cardDismissible}
                            onDismiss={props.onCardDismiss}
                            header={<Text as="h2">{props.cardHeader}</Text>}
                        >
                            <Text color="black">
                                {props.cardTexts.map(text => (
                                    <p key={text}>{parseHtml(text)}</p>
                                ))}
                            </Text>
                        </Card>
                    </div>
                )}

                {props.type === 'intro' && (
                    <MessagesContainer centered={props.introWidth !== 'full'}>
                        <div className={clsx('absolute bottom-0 mt-auto', introWidthClass(props.introWidth ?? 'full'))}>
                            <IntroMessages messages={props.introMessages} />
                        </div>
                    </MessagesContainer>
                )}

                {props.type === 'custom-main' && (
                    <MessagesContainer fullHeight={props.fullHeight}>{props.Main}</MessagesContainer>
                )}

                <div
                    className={clsx(
                        // Above the scroll haze gradient
                        'z-20',
                        // If the main content is full height, the bottom button must be absolutely positioned
                        // so that the main content can scroll behind it.
                        props.type === 'custom-main' && props.fullHeight && 'absolute bottom-0 left-0 w-full',
                    )}
                >
                    <div className="flex w-full justify-center">{props.Button}</div>

                    <div className={twMerge('flex w-full items-center justify-center', spacingBeneathLowestButton)}>
                        {props.Bottom || <span>&nbsp;</span>}
                    </div>
                </div>
            </div>
        </div>
    );
}

export const BottomAlignedWithButton = memo(
    BottomAlignedWithButtonComponent,
) as typeof BottomAlignedWithButtonComponent;
